import { useMemo, useEffect, useCallback } from 'react';
import { useParams, Link, useSearchParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Github, Linkedin, Globe, MapPin, Calendar, GitCommit, Users, Loader2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Separator } from '@/components/ui/separator';
import { Skeleton } from '@/components/ui/skeleton';
import { UserBadges } from '@/components/ui/user-badges';
import { SEOHead } from '@/components/seo';
import ProfileSkeleton from '../components/profile/ProfileSkeleton';
import CommitThreadSkeleton from '../components/commit/CommitThreadSkeleton';
import BranchHighlights from '../components/profile/BranchHighlights';
import { useUser } from '../hooks/useUser';
import { useUserCommits } from '../hooks/useCommits';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';
import { useProfileSEO } from '../hooks/useSEO';
import { useAuth } from '../contexts/AuthContext';
import { useUIStore } from '../stores/uiStore';
import { getAppUrl, getBranchUrl } from '../utils/urls';
import { getNavState } from '../utils/navState';
import { getCached, getUserCacheKey } from '../utils/cache';
import type { User } from '../types/api';
import CommitCard from '../components/commit/CommitCard';
import CommitThread from '../components/commit/CommitThread';
import FollowButton from '../components/social/FollowButton';

interface ProfileProps {
  username?: string;
  initialBranch?: string;
}

export default function Profile({ username: propUsername, initialBranch }: ProfileProps) {
  const { username: paramUsername, branch: paramBranch } = useParams<{ username: string; branch?: string }>();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const username = propUsername || paramUsername;
  const { user: currentUser, isAuthenticated } = useAuth();

  // Read nav state from cookie (for cross-subdomain navigation)
  const navState = useMemo(() => getNavState(), []);
  const passedUser = navState?.user;
  // Use cached user ID to start commits fetch earlier (eliminates waterfall)
  const cachedUser = getCached<User>(getUserCacheKey(username || ''));
  const { user, loading: userLoading, error: userError, updateUser, refetch: refetchUser } = useUser(username || '', { initialData: passedUser, isAuthenticated });

  // Branch from URL only (no store) - URL is single source of truth
  const selectedBranch = initialBranch || paramBranch || null;

  // Get commit sort direction for selected branch (or default to desc)
  // Check both user and cachedUser to get correct direction as early as possible
  const commitSortDirection = useMemo(() => {
    if (selectedBranch) {
      const branches = user?.branches || cachedUser?.branches;
      if (branches) {
        const branch = branches.find((b) => b.branch === selectedBranch);
        if (branch) {
          return branch.commitSortDirection;
        }
      }
    }
    return 'desc';
  }, [selectedBranch, user?.branches, cachedUser?.branches]);

  // Fetch commits with branch filter - backend handles branch filtering and sort direction
  const { commits, loading: commitsLoading, hasMore, loadMore, refetch: refetchCommits } = useUserCommits(
    user?.id || cachedUser?.id || '',
    3,
    {
      branch: selectedBranch,
      sortDirection: commitSortDirection,
    }
  );

  // SEO configuration
  const seoConfig = useProfileSEO(user, selectedBranch);

  // Infinite scroll - always enabled so branch views can load more commits
  const { sentinelRef } = useInfiniteScroll({
    onLoadMore: loadMore,
    hasMore,
    isLoading: commitsLoading,
  });

  // Get store actions
  const openCommitEditor = useUIStore((s) => s.openCommitEditor);
  const openDeleteConfirm = useUIStore((s) => s.openDeleteConfirm);
  const openLinkedInShare = useUIStore((s) => s.openLinkedInShare);
  const openSettings = useUIStore((s) => s.openSettings);
  const subscribeFeed = useUIStore((s) => s.subscribeFeed);
  const subscribeProfile = useUIStore((s) => s.subscribeProfile);
  const deletedCommitIds = useUIStore((s) => s.deletedCommitIds);

  // Filter out deleted commits - backend already handles branch filtering and sorting
  const filteredCommits = useMemo(() => {
    return commits.filter((c) => !deletedCommitIds.has(c.id));
  }, [commits, deletedCommitIds]);

  const isOwnProfile = currentUser?.id === user?.id;

  // Memoize refetch callbacks for stable references
  const handleRefetchUser = useCallback(() => {
    refetchUser();
  }, [refetchUser]);

  const handleRefetchCommits = useCallback(() => {
    refetchCommits();
  }, [refetchCommits]);

  // Subscribe to profile updates (for refetching user data after settings change)
  useEffect(() => {
    if (isOwnProfile) {
      const unsubscribe = subscribeProfile(handleRefetchUser);
      return unsubscribe;
    }
  }, [isOwnProfile, subscribeProfile, handleRefetchUser]);

  // Subscribe to feed updates (for refetching commits after create/edit/delete)
  useEffect(() => {
    const unsubscribe = subscribeFeed(handleRefetchCommits);
    return unsubscribe;
  }, [subscribeFeed, handleRefetchCommits]);

  // Auto-open settings modal when ?settings=open is present (for onboarding)
  useEffect(() => {
    if (isOwnProfile && searchParams.get('settings') === 'open') {
      openSettings();
      // Remove the query param from URL
      setSearchParams({}, { replace: true });
    }
  }, [isOwnProfile, searchParams, setSearchParams, openSettings]);

  const handleBranchSelect = (branch: string | null) => {
    // Update URL to reflect branch selection - URL is source of truth
    // Sort direction is derived from user.branches when branch changes
    if (username) {
      const newUrl = getBranchUrl(username, branch);
      navigate(newUrl, { replace: true });
    }
  };

  const handleFollowChange = (isNowFollowing: boolean) => {
    // Update follower count optimistically
    updateUser({
      followerCount: Math.max(0, (user?.followerCount || 0) + (isNowFollowing ? 1 : -1))
    });
  };

  if (userLoading) {
    return (
      <main className="max-w-[720px] mx-auto px-4">
        <ProfileSkeleton />
      </main>
    );
  }

  if (userError || !user) {
    return (
      <main className="max-w-[720px] mx-auto px-4">
        <div className="flex flex-col items-center justify-center py-20">
          <div className="w-16 h-16 rounded-2xl bg-[hsl(var(--muted))] flex items-center justify-center mb-6">
            <Users className="h-8 w-8 text-[hsl(var(--muted-foreground))]" />
          </div>
          <h2 className="text-xl font-semibold mb-2">User not found</h2>
          <p className="text-[hsl(var(--muted-foreground))] text-center mb-6">
            This user doesn't exist or their profile is private.
          </p>
          <Button asChild>
            <Link to={getAppUrl()}>
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Feed
            </Link>
          </Button>
        </div>
      </main>
    );
  }

  return (
    <>
      <SEOHead config={seoConfig} />
      <main className="max-w-[720px] mx-auto px-4">
        {/* Profile Header Card */}
        <div className="pt-8 pb-4">
          <div className="flex flex-col sm:flex-row gap-6">
            {/* Avatar */}
            <div className="flex-shrink-0">
              <Avatar className="h-24 w-24 sm:h-28 sm:w-28 border-4 border-[hsl(var(--background))] shadow-lg">
                <AvatarImage src={user.avatarUrl} alt={user.displayName || user.username} />
                <AvatarFallback className="text-3xl font-semibold bg-[hsl(var(--muted))]">
                  {user.displayName?.[0] || user.username[0].toUpperCase()}
                </AvatarFallback>
              </Avatar>
            </div>

            {/* Info */}
            <div className="flex-1 min-w-0">
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                <div className="min-w-0">
                  <h1 className="text-2xl font-bold truncate flex items-center gap-2">
                    {user.displayName || user.username}
                    <UserBadges badges={user.badges} className="[&_img]:h-5 [&_img]:w-5" />
                  </h1>
                  <p className="text-[hsl(var(--muted-foreground))] font-mono">
                    @{user.username}
                  </p>
                </div>
                {!isOwnProfile && (
                  <div className="flex-shrink-0">
                    <FollowButton userId={user.id} initialFollowing={user.isFollowing ?? false} onFollowChange={handleFollowChange} requiresAuth={!isAuthenticated} />
                  </div>
                )}
              </div>

              {/* Bio */}
              {user.bio && (
                <p className="mt-3 text-[hsl(var(--foreground))]">{user.bio}</p>
              )}

              {/* Meta info */}
              {user.location && (
                <div className="flex items-center gap-1.5 mt-4 text-sm text-[hsl(var(--muted-foreground))]">
                  <MapPin className="h-4 w-4" />
                  <span>{user.location}</span>
                </div>
              )}

              {/* Social links */}
              {(user.githubUrl || user.linkedinUrl || user.twitterUrl || user.blueskyUrl || user.portfolioUrl) && (
                <div className="flex items-center gap-3 mt-4">
                  {user.linkedinUrl && (
                    <a
                      href={user.linkedinUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--accent))] transition-colors"
                    >
                      <Linkedin className="h-4 w-4" />
                    </a>
                  )}
                  {user.githubUrl && (
                    <a
                      href={user.githubUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--accent))] transition-colors"
                    >
                      <Github className="h-4 w-4" />
                    </a>
                  )}
                  {user.twitterUrl && (
                    <a
                      href={user.twitterUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--accent))] transition-colors"
                    >
                      {/* X (formerly Twitter) icon */}
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z"/>
                      </svg>
                    </a>
                  )}
                  {user.blueskyUrl && (
                    <a
                      href={user.blueskyUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--accent))] transition-colors"
                    >
                      <svg className="h-4 w-4" viewBox="0 0 24 24" fill="currentColor">
                        <path d="M12 10.8c-1.087-2.114-4.046-6.053-6.798-7.995C2.566.944 1.561 1.266.902 1.565.139 1.908 0 3.08 0 3.768c0 .69.378 5.65.601 6.286.505 1.46 1.718 1.945 3.136 1.763 2.128-.272 4.147-.675 4.147-.675l-3.82 3.22c-2.23 1.878-3.893 4.153-3.893 5.872 0 1.368.965 2.766 3.054 2.766 2.142 0 3.967-1.666 5.063-2.88 2.127-2.357 2.94-4.03 3.712-5.853.773 1.823 1.585 3.496 3.712 5.853 1.096 1.214 2.921 2.88 5.063 2.88 2.089 0 3.054-1.398 3.054-2.766 0-1.719-1.663-3.994-3.893-5.872l-3.82-3.22s2.019.403 4.147.675c1.418.182 2.631-.303 3.136-1.763C23.622 9.418 24 4.458 24 3.768c0-.688-.139-1.86-.902-2.203-.659-.299-1.664-.621-4.3 1.24C16.046 4.747 13.087 8.686 12 10.8z"/>
                      </svg>
                    </a>
                  )}
                  {user.portfolioUrl && (
                    <a
                      href={user.portfolioUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="p-2 rounded-lg bg-[hsl(var(--muted))] text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))] hover:bg-[hsl(var(--accent))] transition-colors"
                    >
                      <Globe className="h-4 w-4" />
                    </a>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* Stats - Twitter style */}
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3 sm:gap-0 mt-4 text-sm">
            <div className="flex items-center gap-3 sm:gap-4 flex-wrap">
              {/* Show skeletons if stats aren't loaded yet (optimistic navigation) */}
              {user.commitCount === undefined ? (
                <>
                  <Skeleton className="h-5 w-20" />
                  <Skeleton className="h-5 w-20" />
                  <Skeleton className="h-5 w-20" />
                </>
              ) : (
                <>
                  <div className="flex items-center gap-1">
                    <span className="font-semibold">{user.commitCount || 0}</span>
                    <span className="text-[hsl(var(--muted-foreground))]">commits</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="font-semibold">{user.followerCount || 0}</span>
                    <span className="text-[hsl(var(--muted-foreground))]">followers</span>
                  </div>
                  <div className="flex items-center gap-1">
                    <span className="font-semibold">{user.followingCount || 0}</span>
                    <span className="text-[hsl(var(--muted-foreground))]">following</span>
                  </div>
                </>
              )}
            </div>
            {user.createdAt && (
              <div className="flex items-center gap-1.5 text-[hsl(var(--muted-foreground))]">
                <Calendar className="h-4 w-4" />
                <span>Joined {new Date(user.createdAt).toLocaleDateString('en-US', { month: 'short', year: 'numeric' })}</span>
              </div>
            )}
          </div>
        </div>

        <Separator />

        {/* Branch Highlights - stable once user profile is loaded */}
        <div className="py-4">
          {!user?.branches ? (
            <div className="flex flex-wrap gap-2">
              <Skeleton className="h-8 w-20 rounded-full" />
              <Skeleton className="h-8 w-24 rounded-full" />
              <Skeleton className="h-8 w-28 rounded-full" />
            </div>
          ) : (
            <BranchHighlights
              commits={commits}
              userBranches={user.branches}
              selectedBranch={selectedBranch}
              onSelectBranch={handleBranchSelect}
            />
          )}
        </div>

        {/* Commits Section */}
        <div className="pt-2 pb-6 -mx-4 sm:mx-0">
          {commitsLoading && commits.length === 0 ? (
            <div className="px-4 sm:px-0">
              <CommitThreadSkeleton count={3} />
            </div>
          ) : filteredCommits.length > 0 ? (
            <div>
              <CommitThread hasMore={hasMore}>
                {filteredCommits.map((commit) => (
                  <CommitCard
                    key={commit.id}
                    commit={commit}
                    currentUserId={currentUser?.id}
                    onShare={openLinkedInShare}
                    onEdit={openCommitEditor}
                    onDelete={openDeleteConfirm}
                  />
                ))}
              </CommitThread>

              {/* Infinite scroll sentinel */}
              <div ref={sentinelRef} className="h-px" />

              {/* Loading indicator */}
              {commitsLoading && commits.length > 0 && (
                <div className="flex justify-center py-8">
                  <Loader2 className="h-6 w-6 animate-spin text-[hsl(var(--muted-foreground))]" />
                </div>
              )}
            </div>
          ) : selectedBranch ? (
            <div className="flex flex-col items-center justify-center py-12 text-center px-4 sm:px-0">
              <div className="w-12 h-12 rounded-xl bg-[hsl(var(--muted))] flex items-center justify-center mb-4">
                <GitCommit className="h-6 w-6 text-[hsl(var(--muted-foreground))]" />
              </div>
              <p className="text-[hsl(var(--muted-foreground))]">
                No commits on branch "{selectedBranch}"
              </p>
              <Button
                variant="outline"
                className="mt-4"
                onClick={() => handleBranchSelect(null)}
              >
                Show all commits
              </Button>
            </div>
          ) : (
            <div className="flex flex-col items-center justify-center py-12 text-center px-4 sm:px-0">
              <div className="w-12 h-12 rounded-xl bg-[hsl(var(--muted))] flex items-center justify-center mb-4">
                <GitCommit className="h-6 w-6 text-[hsl(var(--muted-foreground))]" />
              </div>
              <p className="text-[hsl(var(--muted-foreground))]">
                {isOwnProfile ? "You haven't made any commits yet" : "No commits yet"}
              </p>
              {isOwnProfile && (
                <Button className="mt-4" variant="outline" onClick={() => openCommitEditor()}>
                  Create your first commit
                </Button>
              )}
            </div>
          )}
        </div>
      </main>
    </>
  );
}
